import style from './Wallet.module.scss';

// React
import { useEffect } from 'react';

// Ant Design
import { message as antdMessage, Spin } from 'antd';

// Router
import { useNavigate } from 'react-router-dom';

// Images
import tonImg from '../../assets/img/wallet/ton.svg';
import tetherImg from '../../assets/img/wallet/tether.svg';
import bitcoinImg from '../../assets/img/wallet/bitcoin.svg';

// HTTP
import { API_SERVER_WEBSOCKET } from '../../http';

// Websocket
import useWebSocket, { ReadyState } from 'react-use-websocket';

// Redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../redux/store';
import { setRates } from '../../redux/rates/slice';
import { selectRates } from '../../redux/rates/selectors';
import { selectWallets } from '../../redux/wallets/selectors';
import { setNullTransaction } from '../../redux/transaction/slice';
import { setBtcBalance, setTetherBalance, setTonBalance } from '../../redux/wallets/slice';

import WalletAction from '../../components/walletAction/WalletAction';
import { LoadingOutlined } from '@ant-design/icons';


const Wallet = () => {
    // Navigate
    const navigate = useNavigate();

    // Redux
    const dispatch = useAppDispatch();

    // Alert
    const [messageApi, contextHolder] = antdMessage.useMessage();

    // WebSockets Settings
    const token = localStorage.getItem('access_token');


    // Get Wallets
    const { ton_wallet, btc_wallet, usdt_wallet } = useSelector(selectWallets);

    // Get Balance Bitcoin
    const WS_BTC_BALANCE_URL = `${API_SERVER_WEBSOCKET}/bitcoin/balance/ws`;
    const { lastJsonMessage: lastJsonMessageBtcBalance, readyState: readyStateBtcBalance } = useWebSocket(
        WS_BTC_BALANCE_URL,
        {
            share: false,
            reconnectInterval: 5000,
            shouldReconnect: () => true,
            queryParams: {
                'token': token ? token.toString() : '',
            },
        }
    )

    useEffect(() => {
        if (readyStateBtcBalance === ReadyState.OPEN) {
            if (lastJsonMessageBtcBalance && typeof lastJsonMessageBtcBalance === 'string') {
                const balanceBtn = JSON.parse(lastJsonMessageBtcBalance);
                dispatch(setBtcBalance({
                    address: balanceBtn['address'],
                    balance_usd: balanceBtn['balance_usd'],
                    balance_crypto: balanceBtn['balance_crypto'],
                }))
            }
        }
    }, [readyStateBtcBalance, lastJsonMessageBtcBalance, dispatch])

    // Get Balance Ton
    const WS_TON_BALANCE_URL = `${API_SERVER_WEBSOCKET}/ton/balance/ws`;
    const { lastJsonMessage: lastJsonMessageTonBalance, readyState: readyStateTonBalance } = useWebSocket(
        WS_TON_BALANCE_URL,
        {
            share: false,
            reconnectInterval: 5000,
            shouldReconnect: () => true,
            queryParams: {
                'token': token ? token.toString() : '',
            },
        }
    )

    useEffect(() => {
        if (readyStateTonBalance === ReadyState.OPEN) {
            if (lastJsonMessageTonBalance && typeof lastJsonMessageTonBalance === 'string') {
                const balanceTon = JSON.parse(lastJsonMessageTonBalance);
                dispatch(setTonBalance({
                    address: balanceTon['address'],
                    balance_usd: balanceTon['balance_usd'],
                    balance_crypto: balanceTon['balance_crypto'],
                }))
            }
        }
    }, [readyStateTonBalance, lastJsonMessageTonBalance, dispatch])

    // Get Balance Tether
    const WS_TETHER_BALANCE_URL = `${API_SERVER_WEBSOCKET}/usdt/balance/ws`;
    const { lastJsonMessage: lastJsonMessageTetherBalance, readyState: readyStateTetherBalance } = useWebSocket(
        WS_TETHER_BALANCE_URL,
        {
            share: false,
            reconnectInterval: 5000,
            shouldReconnect: () => true,
            queryParams: {
                'token': token ? token.toString() : '',
            },
        }
    )

    useEffect(() => {
        if (readyStateTetherBalance === ReadyState.OPEN) {
            if (lastJsonMessageTetherBalance && typeof lastJsonMessageTetherBalance === 'string') {
                const balanceTether = JSON.parse(lastJsonMessageTetherBalance);
                dispatch(setTetherBalance({
                    address: balanceTether['address'],
                    balance_usd: balanceTether['balance_usd'],
                    balance_crypto: balanceTether['balance_crypto'],
                }))
            }
        }
    }, [readyStateTetherBalance, lastJsonMessageTetherBalance, dispatch])
    
    // Socket Get Rates
    const { tonRub, tonUsd, tetherRub, tetherUsd, bitcoinRub, bitcoinUsd } = useSelector(selectRates);

    const WS_RATE_URL = `${API_SERVER_WEBSOCKET}/rate/ws`;
    const { lastJsonMessage: lastJsonMessageRate, readyState: readyStateRate } = useWebSocket(
        WS_RATE_URL,
        {
            share: false,
            reconnectInterval: 2000,
            shouldReconnect: () => true,
            queryParams: {
                'token': token ? token.toString() : '',
            },
        },
    )

    useEffect(() => {
        if (readyStateRate === ReadyState.OPEN) {
            if (lastJsonMessageRate && typeof lastJsonMessageRate === 'string') {
                const rates = JSON.parse(lastJsonMessageRate);
                dispatch(setRates({
                    tonUsd: rates['the-open-network/usd']?.rate,
                    tonRub: rates['the-open-network/rub']?.rate,
                    bitcoinUsd: rates['bitcoin/usd']?.rate,
                    bitcoinRub: rates['bitcoin/rub']?.rate,
                    tetherUsd: rates['tether/usd']?.rate,
                    tetherRub: rates['tether/rub']?.rate,
                }));
            }
        }
    }, [readyStateRate, lastJsonMessageRate, dispatch])

    // Balance Total
    const totalAmountBalance = btc_wallet && bitcoinRub && ton_wallet && tonRub && usdt_wallet && tetherRub && ((btc_wallet?.balance_crypto * bitcoinRub) + (ton_wallet?.balance_crypto * tonRub) + (usdt_wallet?.balance_crypto * tetherRub)).toFixed(2);

    const handleGoToGetMoney = () => {
        if (totalAmountBalance) {
            navigate('/home/card');
        }
    }

    const handleGoToCurrencyInfo = (cryptoName: string) => {
        if (totalAmountBalance) {
            navigate(`/home/currency-info/${cryptoName}/`);
        }
    }

    useEffect(() => {
        dispatch(setNullTransaction());
    }, [])

    return (
        <div className={style.wallet}>
            {contextHolder}
            <div className={style.wallet__header}>
                <h2>Кредитный лимит</h2>
                <div className={style.wallet__header__total_price}>{totalAmountBalance ? totalAmountBalance : <Spin indicator={<LoadingOutlined style={{color: '#000'}} spin />} size='small' />} <span>₽</span></div>
                <button onClick={() => handleGoToGetMoney()}>Получить средства</button>
            </div>
            <WalletAction cryptoName={undefined} />
            <div className={style.wallet__currencies}>
                <div className={style.wallet__currencies__currency} onClick={() => handleGoToCurrencyInfo('ton')}>
                    <div className={style.wallet__currencies__currency__image}>
                        <img src={tonImg} alt="ton" />
                        <p>TON</p>
                    </div>
                    <div className={style.wallet__currencies__currency__value}>
                        <p>{ton_wallet && tonRub && (tonRub * ton_wallet.balance_crypto).toFixed(2)} ₽</p>
                        <p>{ton_wallet?.balance_crypto.toFixed(4)} TON</p>
                    </div>
                </div>
                <div className={style.wallet__currencies__currency} onClick={() => handleGoToCurrencyInfo('usdt')}>
                    <div className={style.wallet__currencies__currency__image}>
                        <img src={tetherImg} alt="tether" />
                        <p>USDT</p>
                    </div>
                    <div className={style.wallet__currencies__currency__value}>
                        <p>{usdt_wallet && tetherRub && (tetherRub * usdt_wallet.balance_crypto).toFixed(2)} ₽</p>
                        <p>{usdt_wallet?.balance_crypto.toFixed(4)} USDT</p>
                    </div>
                </div>
                <div className={style.wallet__currencies__currency} onClick={() => handleGoToCurrencyInfo('btc')}>
                    <div className={style.wallet__currencies__currency__image}>
                        <img src={bitcoinImg} alt="bitcoin" />
                        <p>BTC</p>
                    </div>
                    <div className={style.wallet__currencies__currency__value}>
                        <p>{btc_wallet && bitcoinRub && (bitcoinRub * btc_wallet?.balance_crypto).toFixed(2)} ₽</p>
                        <p>{btc_wallet?.balance_crypto} BTC</p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Wallet;